import * as ko from "knockout";
import * as L from "../mapbox";

import { Bounds, FeatureType, Feature, Layer, LatLng, MapAdapter } from "./api";
import { ClusterPlugin, createFeature, ColorWheel, GeoJSONLayer, LayerAddedCallback } from "./common";
import { LandLayer } from "./landLayer";
import { MapViewModel } from "./mapViewModel";
import { WellLayer } from "./wellLayer";


const PandellDlsTileUrl = "http://maptiles.pandell.com/v2/DLS/{z}/{x}/{y}.png";
const PandellMapboxAccessToken = "pk.eyJ1IjoicGFuZGVsbC10ZXN0IiwiYSI6ImZyOVE4TzAifQ.3nl0eDsG7FusR7Hegqw0hw";
const PandellMapboxTerrainTileUrl = "pandell-test.ii2j3i26";
const PandellMapboxSatelliteTileUrl = "pandell-test.158o90k7";


function createColorWheel(colors: string[]): ColorWheel {
    let markerOrdinal = 0;
    return () => {
        markerOrdinal += 1;
        return colors[markerOrdinal % colors.length];
    };
}


function getFeatures<TProperties, TLayer extends GeoJSONLayer<L.ILayer>>(layer: L.mapbox.FeatureLayerT<TLayer>, map: L.Map, type: FeatureType, propFilter?: (a: TProperties) => boolean): Feature[] {
    if (layer) {
        const featureLayers = layer.getLayers();
        const filteredLayers = (propFilter
            ? featureLayers.filter(l => propFilter(l.feature.properties))
            : featureLayers);
        return filteredLayers.map(l => createFeature(l, map, type));
    }
    return [];
}


function createMapInstance(element: HTMLElement, options: MapboxOptions, adapter: MapboxAdapter): L.Map {

    // -------------------------------------------
    // Mapbox initialization
    L.mapbox.accessToken = options.mapboxAccessToken;

    const { allowDraw, drawnLayer, showDlsGrid, showSatelliteMap, terrainTileUrl, satelliteTileUrl } = options;

    const terrainLayer = L.mapbox.tileLayer(terrainTileUrl);
    const dlsGridLayer = L.tileLayer(options.dlsTileUrl, {
        attribution: "Pandell Tiles",
        minZoom: 8,
        maxNativeZoom: 14,
        opacity: 0.4
    });

    // null second parameter is the layer, which we specify in the
    // options (in the case we have two layers, terrain and DLS)
    const map = L.mapbox.map(element, null, {
        attributionControl: false,
        doubleClickZoom: options.doubleClickZoom,
        center: <L.LatLng>options.initialLatLong,
        minZoom: options.minZoom,
        maxZoom: options.maxZoom,
        zoom: options.initialZoom,
        layers: showDlsGrid ? [terrainLayer, dlsGridLayer] : [terrainLayer]
    });

    const baseMaps: { [id: string]: L.TileLayer } = {
        "Terrain": terrainLayer
    };
    if (showSatelliteMap) {
        baseMaps["Satellite"] = L.mapbox.tileLayer(satelliteTileUrl);
    }

    const overlayMaps = showDlsGrid ? {
        "DLS Grid": dlsGridLayer
    } : null;

    if (showSatelliteMap || showDlsGrid) {
        L.control.layers(baseMaps, overlayMaps).addTo(map);
    }

    // -------------------------------------------
    // Minimap
    const showMinimap = options.showMinimap;
    let miniMap: L.Control.MiniMapControl;

    // TODO: refactor to avoid side effects
    ko.computed(function toggleMinimap(): void {
        if (ko.unwrap(showMinimap)) {
            if (!miniMap) {
                const miniMapTerrainLayer = L.mapbox.tileLayer(terrainTileUrl);
                miniMap = L.control.minimap(miniMapTerrainLayer, {
                    autoToggleDisplay: true,
                    toggleDisplay: true,
                    zoomLevelOffset: -5,
                    width: 200,
                    height: 200
                });
            }
            map.addControl(miniMap);
        } else {
            if (miniMap) {
                map.removeControl(miniMap);
            }
        }
    });

    // Leaflet.Draw
    if (allowDraw) {
        const drawOptions = options.drawOptions;
        const drawControl = new L.Control.Draw({
            draw: {
                polyline: drawOptions.drawPolyline
                    ?
                        drawOptions.colorPolyline
                        ? {
                            shapeOptions: {
                                color: drawOptions.colorPolyline
                            }
                        }
                        : {}
                    : false,
                polygon: drawOptions.drawPolygon
                    ?
                        drawOptions.colorPolygon
                        ? {
                            shapeOptions: {
                                color: drawOptions.colorPolygon
                            }
                        }
                        : {}
                    : false,
                rectangle: drawOptions.drawRectangle
                    ?
                        drawOptions.colorRectangle
                        ? {
                            shapeOptions: {
                                color: drawOptions.colorRectangle
                            }
                        }
                        : {}
                    : false,
                circle: drawOptions.drawCircle
                    ?
                        drawOptions.colorCircle
                        ? {
                            shapeOptions: {
                                color: drawOptions.colorCircle
                            }
                        }
                        : {}
                    : false,
                marker: drawOptions.drawMarker ? {} : false
            },
            edit: {
                featureGroup: drawnLayer,
                edit: drawOptions.editShapes,
                remove: drawOptions.deleteShapes
            }
        });

        map.addLayer(drawnLayer);
        map.addControl(drawControl);
        map.on("draw:created", function(e: L.LeafletLayerEvent): void {
            drawnLayer.addLayer(e.layer);
            adapter.drawnObjects(drawnLayer.toGeoJSON());
        });
        map.on("draw:edited", function(e: L.LeafletLayerEvent): void {
            adapter.drawnObjects(drawnLayer.toGeoJSON());
        });
        map.on("draw:deleted", function(e: L.LeafletLayerEvent): void {
            adapter.drawnObjects(drawnLayer.toGeoJSON());
        });
    }

    const moveendQueue: (() => void)[] = [];

    // -------------------------------------------
    // Bounds & Lat/long
    map.on("moveend", function(): void {
        const center = map.getCenter();
        adapter.latLong({ lat: center.lat, lng: center.lng });

        const mapBounds = map.getBounds();
        adapter.bounds({ northEast: mapBounds.getNorthEast(), southWest: mapBounds.getSouthWest() });

        while (moveendQueue.length > 0) {
            const action = moveendQueue.pop();
            action();
        }
    });


    // -------------------------------------------
    // Zoom
    map.on("zoomend", function(): void {
        adapter.zoom(map.getZoom());
    });


    // -------------------------------------------
    // Fullscreen
    const hashChangeEvent = "hashchange";
    const hashChangeListener = () => map.toggleFullscreen();

    map.on("fullscreenchange", () => {
        // We can't call the adapter.onFullscreenToggle callback here because the map hasn't finished moving/resizing yet
        // and won't be guaranteed to be resized. Call the callback on "moveend" event.
        moveendQueue.push(() => adapter.onFullscreenToggle());

        if (window && window.addEventListener) {
            if (map.isFullscreen()) {
                // Exit fullscreen on hash change (popup clicked, etc.)
                window.addEventListener(hashChangeEvent, hashChangeListener, false);
            } else {
                window.removeEventListener(hashChangeEvent, hashChangeListener, false);
            }
        }
    });


    return map;
}


interface MapboxOptions {
    allowDraw?: boolean;
    clusterPlugin?: ClusterPlugin;
    dialogOptions?: LeafletDialogOptions;
    dlsTileUrl?: string;
    doubleClickZoom?: boolean;
    drawOptions?: LeafletDrawOptions;
    drawnLayer: L.FeatureGroup<L.ILayer>;
    initialLatLong?: LatLng;
    initialZoom?: number;
    landMarkerThreshold?: number;
    loadFeaturesByArray?: boolean;
    mapboxAccessToken?: string;
    maxZoom?: number;
    minZoom?: number;
    onMapCreated: (map: L.Map) => void;
    satelliteTileUrl?: string;
    showDialog: boolean;
    showDlsGrid: boolean;
    showMinimap: ko.Observable<boolean>;
    showSatelliteMap: boolean;
    terrainTileUrl?: string;
}

interface LeafletDialogOptions {
    anchor?: number[];
    initOpen?: boolean;
    position?: string;
    size?: number[];
}

interface LeafletDrawOptions {
    colorCircle?: string;
    colorPolygon?: string;
    colorPolyline?: string;
    colorRectangle?: string;
    deleteShapes?: boolean;
    drawCircle?: boolean;
    drawMarker?: boolean;
    drawPolygon?: boolean;
    drawPolyline?: boolean;
    drawRectangle?: boolean;
    editShapes?: boolean;
}


export class MapboxAdapter implements MapAdapter {
    bounds: ko.Observable<Bounds>;
    latLong: ko.Observable<LatLng>;
    zoom: ko.Observable<number>;
    drawnObjects: ko.Observable<GeoJSON.FeatureCollection<GeoJSON.GeometryObject>>;

    viewModel: ko.Observable<MapViewModel>;

    private _map: L.Map;
    private _dialogControl: L.Control.Dialog;
    private _dialogContentSubscription: ko.subscription<string>;
    private _dialogOptions: LeafletDialogOptions;
    private _dialogVisibleSubscription: ko.subscription<boolean>;
    private _layerSubscription: ko.subscription<any>;
    private _lastCenter: Feature | FeatureType;
    private _lastZoomToBounds: boolean;
    private _landLayer: LandLayer;
    private _wellLayer: WellLayer;
    private _drawnLayer: L.FeatureGroup<any>;
    private _fullscreenControl: L.Control.Fullscreen;
    private _resetFocusControl: L.Control;
    private _onMarkerClick: (e: L.LeafletEvent) => void;


    constructor(element: HTMLElement, initialViewModel: MapViewModel, bindingOptions: Object) {

        const options = ko.utils.extend({
            allowDraw: false,
            dlsTileUrl: PandellDlsTileUrl,
            doubleClickZoom: false,
            initialLatLong: { lat: 53.52617, lng: -113.492175 },
            initialZoom: 4,
            loadFeaturesByArray: false,
            mapboxAccessToken: PandellMapboxAccessToken,
            maxZoom: 16,
            minZoom: 4,
            satelliteTileUrl: PandellMapboxSatelliteTileUrl,
            showDlsGrid: true,
            showMinimap: ko.observable(false),
            showSatelliteMap: false,
            terrainTileUrl: PandellMapboxTerrainTileUrl
        }, <MapboxOptions>ko.unwrap(bindingOptions));
        options.drawOptions = ko.utils.extend({
            editShapes: true,
            deleteShapes: true,
            drawPolygon: true,
            drawPolyline: true,
            drawRectangle: true,
            drawCircle: true,
            drawMarker: true
        }, <LeafletDrawOptions>ko.unwrap((<any>bindingOptions).drawOptions));
        options.dialogOptions = ko.utils.extend({
            anchor: [-150, 0],
            size: [150, 0],
            position: "bottomleft",
            initOpen: false
        }, <LeafletDialogOptions>ko.unwrap((<any>bindingOptions).dialogOptions));

        const clusterPluginName: string = (<any>bindingOptions).clusterPlugin || "MarkerCluster";
        const clusterPlugin: ClusterPlugin = (<any>ClusterPlugin)[clusterPluginName];

        if (clusterPlugin !== null && clusterPlugin !== undefined) {
            options.clusterPlugin = clusterPlugin;
        } else {
            throw new Error(`Unsupported ClusterPlugin: ${clusterPluginName}`);
        }

        this.bounds = ko.observable<Bounds>(null);
        this.drawnObjects = ko.observable<GeoJSON.FeatureCollection<GeoJSON.GeometryObject>>(null);
        this.latLong = ko.observable(options.initialLatLong);
        this.zoom = ko.observable(options.initialZoom);

        this.viewModel = ko.observable<MapViewModel>(initialViewModel);
        this.viewModel.subscribe(this._viewModelConnected, this);
        this.viewModel.subscribe(this._viewModelDisconnected, this, "beforeChange");

        this._lastCenter = null;
        this._map = null;
        this._onMarkerClick = null;
        this._dialogOptions = options.dialogOptions;

        this._landLayer = new LandLayer(options);
        this._wellLayer = new WellLayer(options);

        if (options.allowDraw) {
            this._drawnLayer = L.featureGroup();
            options.drawnLayer = this._drawnLayer;
        }


        this.zoom.subscribe(() => this._landLayer.toggleLandMarkers(this._map), this);

        // HACKITY HACK HACK:
        // If the map is rendered to an element that is not visible, it
        // may appear in the wrong spot when it does become visible. This
        // can occur when loading a form, for example, and in PLI, it is very
        // difficult to detect when the form is actually visible. Hence, this
        // ridiculous hack. Any other ideas?
        const interval = setInterval(() => {
            if (element.offsetParent !== null) { // element is visible
                clearInterval(interval);

                const viewModel = this.viewModel();
                this._map = createMapInstance(element, options, this);
                this._layersUpdated(viewModel.layers());

                const bounds = viewModel.bounds();
                if (bounds) {
                    this._updateBounds(bounds);
                } else if (this._lastCenter !== null && this._hasLayer(this._lastCenter)) {
                    this.center(this._lastCenter, this._lastZoomToBounds);
                } else {
                    this._map.setView(viewModel.latLong(), viewModel.zoom());
                }

                if (viewModel.onResetFocus) {
                    this._map.addControl(this._getResetFocusControl());
                }

                if (viewModel.showFullscreenButton) {
                    this._map.addControl(this._getFullscreenControl());
                }

                if (typeof options.onMapCreated === "function") {
                    options.onMapCreated(this._map);
                }

                if (options.showDialog){
                    this._map.addControl(this._getDialog());
                }
            }
        }, 100);
    }


    center(feature: (Feature|FeatureType), zoomToBounds: boolean): void {
        this._lastCenter = feature;
        this._lastZoomToBounds = zoomToBounds;

        if (!this._map) {
            return;
        }

        const featureType = typeof feature === "number" ? feature : feature.type;
        const mapLayer = this._getLayerByFeatureType(featureType);
        if (!mapLayer) {
            throw new Error("Layer for feature type '" + featureType + "' does not exist.");
        }

        const bounds = mapLayer.getBounds();
        this._updateBounds({ northEast: bounds.getNorthEast(), southWest: bounds.getSouthWest(), zoom: zoomToBounds });
    }


    getFeatures<TProperties>(type: FeatureType, propFilter?: (a: TProperties) => boolean): Feature[] {
        const mapLayer = this._getLayerByFeatureType(type);
        return mapLayer
            ? getFeatures(mapLayer, this._map, type, propFilter)
            : [];
    }


    onFullscreenToggle(): void {
        const viewModel = this.viewModel();

        if (viewModel.onFullscreenToggle) {
            viewModel.onFullscreenToggle(this._map.isFullscreen());
        }
    }


    private _getFullscreenControl(): L.Control.Fullscreen {
        if (!this._fullscreenControl) {
            this._fullscreenControl = L.control.fullscreen({ position: "topleft" });
        }

        return this._fullscreenControl;
    }


    private _getResetFocusControl(): L.Control {
        if (this._resetFocusControl) {
            return this._resetFocusControl;
        }

        this._resetFocusControl = new L.Control({ position: "topleft" });
        this._resetFocusControl.onAdd = (map: L.Map) => {
            const resetDiv = L.DomUtil.create("div", "leaflet-control-reset leaflet-bar");
            const resetA = L.DomUtil.create("a", "", resetDiv);
            L.DomUtil.create("i", "", resetA);

            L.DomEvent
                .addListener(resetA, "click", (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    const viewModel = this.viewModel();
                    if (viewModel && viewModel.onResetFocus) {
                        viewModel.onResetFocus();
                    }
                 });
            resetA.title = "Reset map";

            return resetDiv;
        };

        return this._resetFocusControl;
    }


    private _getDialog(): L.Control.Dialog {
        if (!this._dialogControl) {
            this._dialogControl = L.control.dialog(this._dialogOptions);
        }

        return this._dialogControl;
    }


    private _addLayer(layer: L.ILayer): void {
        if (!layer || !this._map) { return; }
        this._map.addLayer(layer);
    }


    private _createFeatureAddCallback(layer: Layer): LayerAddedCallback {
        const colorWheel = createColorWheel(this.viewModel().colors);

        switch (layer.featureType) {
            case FeatureType.Land:
                return this._landLayer.createLayerAdded(this._map, colorWheel, layer.featureAddCallback);
            case FeatureType.Well:
                return this._wellLayer.createLayerAdded(this._map, layer.featureAddCallback);
            default:
                throw new Error("Unsupported feature type: '" + layer.featureType + "'");
        }
    }


    private _getLayerByFeatureType(featureType: FeatureType): L.mapbox.FeatureLayerT<any> {
        switch (featureType) {
            case FeatureType.Land:
                return this._landLayer.getFeatureLayer();
            case FeatureType.Well:
                return this._wellLayer.getFeatureLayer();
            default:
                throw new Error("Unsupported feature type: '" + featureType + "'");
        }
    }


    private _hasLayer(feature: (FeatureType | Feature)): boolean {
        if (typeof feature === "number") {
            return this._getLayerByFeatureType(feature) !== null;
        } else {
            return this._getLayerByFeatureType(feature.type) !== null;
        }
    }


    private _layersUpdated(layers: Layer[]): void {
        // Remove previous layers
        if (!this._map) { return; }

        this._map.closePopup();

        // Remove layers from map
        this._removeLayer(this._landLayer.getFeatureLayer());
        this._removeLayer(this._wellLayer.getMarkerClusterGroup());

        // Set the underlying feature layer data to null
        // This will create a new and empty feature layer
        this._landLayer.resetFeatureLayer();
        this._wellLayer.resetFeatureLayer();

        // Create new layers
        for (const layer of layers) {
            switch (layer.featureType) {
                case FeatureType.Land:
                    this._landLayer.setFeatureLayer(layer.geoData as GeoJSON.FeatureCollection<GeoJSON.GeometryObject>, this._createFeatureAddCallback(layer));

                    // Populate land layer on map
                    this._addLayer(this._landLayer.getFeatureLayer());
                    this._landLayer.toggleLandMarkersOnLayerUpdate(this._map);
                    break;
                case FeatureType.Well:
                    this._wellLayer.setFeatureLayer(layer.geoData, this._createFeatureAddCallback(layer), this._map, this._onMarkerClick);

                    // Populate well layer on map
                    this._addLayer(this._wellLayer.getMarkerClusterGroup());
                    break;
                default:
                    throw new Error("Unsupported feature type: '" + layer.featureType + "'");
            }
        }
    }


    private _removeLayer(layer: L.ILayer): void {
        if (!layer || !this._map) { return; }
        this._map.removeLayer(layer);
    }


    private _updateBounds(bounds: Bounds): void {
        if (bounds.northEast && bounds.southWest) {
            this.bounds(bounds);
            if (this._map) {
                const latLngBounds = L.latLngBounds(bounds.southWest, bounds.northEast);
                if (bounds.zoom) {
                    this._map.fitBounds(latLngBounds);
                } else {
                    this._map.setView(latLngBounds.getCenter());
                }
            }
        }
    }


    private _updateSummaryContent(contentHtml: string): void {
        const dialog = this._getDialog();

        if (contentHtml.length > 0) {
            dialog.setContent(contentHtml);
            dialog.update();
        }
    }


    private _updateSummaryVisibility(visible: boolean): void {
        const dialog = this._getDialog();

        if (visible) {
            dialog.open();
        } else {
            dialog.close();
        }
    }


    private _viewModelConnected(viewModel: MapViewModel): void {
        viewModel.adapter(this);
        this._layerSubscription = viewModel.layers.subscribe(this._layersUpdated, this);
        this._dialogContentSubscription = viewModel.dialogContent.subscribe(this._updateSummaryContent, this);
        this._dialogVisibleSubscription = viewModel.dialogVisible.subscribe(this._updateSummaryVisibility, this);
        this._layersUpdated(viewModel.layers());
        if (viewModel.onMarkerClick) {
            this._onMarkerClick = viewModel.onMarkerClick;
        }
        if (viewModel.onResetFocus && this._map) {
            this._map.addControl(this._getResetFocusControl());
        }
        if (viewModel.showFullscreenButton && this._map) {
            this._map.addControl(this._getFullscreenControl());
        }
    }


    private _viewModelDisconnected(viewModel: MapViewModel): void {
        viewModel.adapter(null);
        if (this._layerSubscription) { this._layerSubscription.dispose(); }
        if (this._dialogContentSubscription) { this._dialogContentSubscription.dispose(); }
        if (this._dialogVisibleSubscription) { this._dialogVisibleSubscription.dispose(); }
        if (this._onMarkerClick) {
            this._onMarkerClick = null;
            if (this._wellLayer) {
                this._wellLayer.clearClickHandler();
            }
        }
        if (this._map && this._resetFocusControl) {
            this._map.removeControl(this._resetFocusControl);
        }
        if (this._map && this._fullscreenControl) {
            this._map.removeControl(this._fullscreenControl);
        }
    }
}



// WEBPACK FOOTER //
// mapboxAdapter.ts